import { markBatchQc, type BucketedBatchLine, type BucketedItemBalance } from '@api/vendor-portal';
import { DataSheet, type DataSheetColumn } from '@components/ui/DataSheet';
import StatusPill, { type StatusTone } from '@components/ui/StatusPill';
import useToaster from '@hooks/toaster/useToaster';
import { Button, Drawer, Group, Text } from '@mantine/core';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { neutralColors } from '@styles/design-system';
import { formatNumber } from '@utils/materialQuantity';
import { type FC, useMemo, useState } from 'react';

import BatchActionModal, { type BatchAction } from './BatchActionModal';

const AVAILABILITY_LABEL: Record<string, string> = {
    USABLE: 'QC approved',
    IN_QC: 'In QC',
    BLOCKED: 'Rejected',
};

const AVAILABILITY_TONE: Record<string, StatusTone> = {
    USABLE: 'positive',
    IN_QC: 'warning',
    BLOCKED: 'critical',
};

/** `dd-MMM-yyyy`, which reads unambiguously wherever the reader is. */
function formatDate(value: string | null): string {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${String(date.getDate()).padStart(2, '0')}-${months[date.getMonth()]}-${date.getFullYear()}`;
}

/** A line of the panel: a batch, the no-batch bucket, or the item's bad stock. */
type PanelLine = BucketedBatchLine & { bad?: boolean };

/** A batch that has gone off is blocked by expiry, not by a verdict — QC cannot undo that. */
function isExpired(batch: BucketedBatchLine): boolean {
    if (!batch.expiryDate) return false;
    const expiry = new Date(batch.expiryDate);
    return !Number.isNaN(expiry.getTime()) && expiry < new Date();
}

function stateLabel(value: string, row: PanelLine): string {
    if (row.bad) return 'Bad stock';
    return isExpired(row) ? 'Expired' : (AVAILABILITY_LABEL[value] ?? value);
}

interface BatchDetailPanelProps {
    /**
     * Offered on finished goods only. Material leaves by being consumed, returned or written
     * off — a site does not "dispatch" the flour it was sent.
     */
    onDispatch?: (batch: BucketedBatchLine) => void;
    item: BucketedItemBalance | null;
    onClose: () => void;
    onChanged: () => void;
    /**
     * The brand reading a site's stock, rather than the site working its own.
     *
     * Every action in this panel writes through the vendor door — the API resolves the site
     * from the login — so fired from a brand session they would all fail. The brand's verdicts
     * have their own home (the Batches tab's QC panel, through the on-behalf door); here it
     * gets the same breakdown the site sees, with the buttons that are not its to press gone.
     */
    readOnly?: boolean;
    /**
     * Correcting one batch's figure.
     *
     * Offered even in readOnly, unlike the QC and disposal actions beside it: those write
     * through the vendor door and would fail from a brand session, while a correction is the
     * brand's own act and has its own door. Absent means no column, as with dispatch.
     */
    onAdjust?: (batch: BucketedBatchLine) => void;
    /**
     * The batch's history - what went into it, what it went into, where it was sent.
     *
     * Offered in every state and in readOnly, because reading is never the site's to be
     * denied. Absent means no button, as with dispatch.
     */
    onHistory?: (batch: BucketedBatchLine) => void;
}

/**
 * The batches behind one item's figures, and what can be done to them.
 *
 * Opened from a row rather than shown inline, because the item totals are what a person scans
 * and the batches are what they need once something looks wrong. A batch-per-row table would
 * have repeated every item name down the screen and buried the number that matters.
 *
 * Blocked batches are listed alongside the rest rather than filtered out. Expired and rejected
 * material is really there, it has to be accounted for, and a stock screen that quietly omitted
 * it would leave someone unable to reconcile what they can see on the floor.
 *
 * THE ACTIONS OFFERED DEPEND ON THE STATE, because a person on the floor should not have to
 * work out which of six verbs applies to the row in front of them. A batch waiting on QC gets
 * pass and fail. A usable batch gets fail, which is what a recall is. A blocked batch gets the
 * two ways out — release it, or record it leaving. Everything that needs an explanation opens a
 * box asking for one; everything that does not fires on the click, because making someone
 * confirm an ordinary QC pass forty times a day teaches them to stop reading dialogs.
 */
const BatchDetailPanel: FC<BatchDetailPanelProps> = ({
    item,
    onClose,
    onChanged,
    onDispatch,
    readOnly = false,
    onAdjust,
    onHistory,
}) => {
    const { showToastSuccess, showToastError } = useToaster();

    const [action, setAction] = useState<BatchAction | null>(null);
    const [actionBatch, setActionBatch] = useState<BucketedBatchLine | null>(null);
    const [pendingBatchId, setPendingBatchId] = useState<string | null>(null);

    const isMaterial = item?.itemType?.toUpperCase() === 'MATERIAL';

    /**
     * Whether this site may rule on this item at all.
     *
     * Material, yes — the site checks it at its own gate, and consumption draws only on
     * released stock, so a floor that cannot release a drum cannot run. Finished goods, no:
     * quality on what leaves is the brand's signature, and a manufacturer able to pass its own
     * output is exactly the control the brand is buying this to have.
     *
     * Removing stock stays available either way. Writing off a blocked batch is not a verdict,
     * it is recording that the goods physically went in the bin — and the site is the only
     * party who can see that happen.
     */
    const canRuleOnQc = isMaterial;

    /** The verdicts that need no justification, fired straight from the row. */
    const submitVerdict = async (batch: BucketedBatchLine, qcStatus: 'PASSED' | 'FAILED') => {
        setPendingBatchId(batch.batchId);
        try {
            // `notable` is the server's answer to "will the brand be shown this?" — it is not
            // inferred here, because the rule for what counts as notable lives with the rule
            // that demands a reason for it, and the two must not drift apart.
            const { notable } = await markBatchQc(batch.batchId, { qcStatus });
            showToastSuccess({
                title: `${batch.batchCode ?? 'Batch'} marked ${qcStatus === 'PASSED' ? 'passed' : 'failed'}`,
                subtitle: notable
                    ? 'This reverses a failure, and the brand is shown it.'
                    : qcStatus === 'PASSED'
                        ? 'It can now be used in production.'
                        : 'It is blocked and cannot be used until released.',
            });
            onChanged();
        } catch (err: any) {
            showToastError({
                title: 'Could not record the verdict',
                subtitle: err?.error?.message ?? err?.message ?? 'Something went wrong.',
            });
        } finally {
            setPendingBatchId(null);
        }
    };

    const openAction = (batch: BucketedBatchLine, next: BatchAction) => {
        setActionBatch(batch);
        setAction(next);
    };

    const columns = useMemo<DataSheetColumn<PanelLine>[]>(
        () => [
            {
                key: 'batchCode',
                header: 'Batch',
                width: 150,
                sticky: true,
                render: (value: string | null) => value ?? 'No batch',
            },
            {
                key: 'quantity',
                header: 'Quantity',
                width: 90,
                align: 'right',
                // One decimal at most, as on the balances it sums to. The export keeps the raw figure.
                render: (value: number) => formatNumber(value ?? 0, 1),
            },
            {
                key: 'availability',
                header: 'State',
                // Wide enough for "QC approved" as a pill: uppercase, spaced, padded.
                width: 130,
                // Expiry blocks a batch QC passed, so "Rejected" would read as a verdict nobody gave.
                render: (value: string, row) => (
                    <StatusPill
                        label={stateLabel(value, row)}
                        tone={AVAILABILITY_TONE[value] ?? 'neutral'}
                    />
                ),
                csvValue: (value: string, row) => stateLabel(value, row),
            },
            {
                key: 'qcStatus',
                header: 'QC',
                width: 90,
                render: (value: string | null) => value ?? '—',
            },
            {
                key: 'expiryDate',
                header: 'Expires',
                width: 110,
                render: (value: string | null) => formatDate(value),
                csvValue: (value: string | null) => value ?? '',
            },
            {
                key: 'actions',
                // Headed, because the column's only content used to be a lone word with no
                // indication of what it acted on or what it would do.
                header: 'Actions',
                width: 250,
                // Nothing to export: these are buttons, not a value the row has.
                csvValue: () => '',
                render: (_value, row) => {
                    // Kept bad at the gate: nothing to act on from here yet.
                    if (row.bad) return null;
                    // The no-batch bucket is not a batch — there is nothing to pass, fail,
                    // dispose of or trace, because no batch row exists behind it. Its figure
                    // can still be wrong, though, and Adjust is the only way to correct it.
                    if (!row.batchId) {
                        return (
                            onAdjust && (
                                <Button
                                    size="xs"
                                    variant={ButtonVariant.DEFAULT}
                                    onClick={() => onAdjust(row)}
                                >
                                    Adjust
                                </Button>
                            )
                        );
                    }

                    const busy = pendingBatchId === row.batchId;
                    const expired = isExpired(row);

                    // Offered on every batch state, unlike the rest: a count can disagree
                    // about stock waiting on QC or blocked just as easily as released stock.
                    const adjustButton = onAdjust && (
                        <Button
                            size="xs"
                            variant={ButtonVariant.DEFAULT}
                            onClick={() => onAdjust(row)}
                        >
                            Adjust
                        </Button>
                    );

                    const historyButton = onHistory && (
                        <Button
                            size="xs"
                            variant={ButtonVariant.SUBTLE}
                            onClick={() => onHistory(row)}
                        >
                            History
                        </Button>
                    );

                    const sendButton = onDispatch && row.availability !== 'BLOCKED' && (
                        <Button
                            size="xs"
                            variant={ButtonVariant.ACTION}
                            onClick={() => onDispatch(row)}
                        >
                            Send
                        </Button>
                    );

                    if (row.availability === 'IN_QC') {
                        return (
                            <Group gap={8} wrap="nowrap">
                                {historyButton}
                                {adjustButton}
                                {sendButton}
                                {canRuleOnQc && (
                                    <>
                                        <Button
                                            size="xs"
                                            variant={ButtonVariant.ACTION}
                                            loading={busy}
                                            onClick={() => submitVerdict(row, 'PASSED')}
                                        >
                                            Pass QC
                                        </Button>
                                        <Button
                                            size="xs"
                                            variant={ButtonVariant.OUTLINED_DESTRUCTIVE}
                                            loading={busy}
                                            onClick={() => submitVerdict(row, 'FAILED')}
                                        >
                                            Fail QC
                                        </Button>
                                    </>
                                )}
                            </Group>
                        );
                    }

                    if (row.availability === 'BLOCKED') {
                        return (
                            <Group gap={8} wrap="nowrap">
                                {historyButton}
                                {/* An expired batch cannot be released by a QC verdict — the
                                    date is what blocks it, and only removing it is honest. */}
                                {!expired && canRuleOnQc && (
                                    <Button
                                        size="xs"
                                        variant={ButtonVariant.DEFAULT}
                                        onClick={() => openAction(row, 'RELEASE')}
                                    >
                                        Release
                                    </Button>
                                )}
                                <Button
                                    size="xs"
                                    variant={ButtonVariant.OUTLINED_DESTRUCTIVE}
                                    onClick={() => openAction(row, 'REMOVE')}
                                >
                                    Remove
                                </Button>
                            </Group>
                        );
                    }

                    return (
                        <Group gap={8} wrap="nowrap">
                            {historyButton}
                            {adjustButton}
                            {sendButton}
                            {/* Already released and in use, so this is the recall case: it
                                blocks the stock at once and cannot be explained after the fact.
                                Through the modal, which makes it deliberate and takes a reason. */}
                            {canRuleOnQc && (
                                <Button
                                    size="xs"
                                    variant={ButtonVariant.OUTLINED_DESTRUCTIVE}
                                    loading={busy}
                                    onClick={() => openAction(row, 'FAIL')}
                                >
                                    Fail QC
                                </Button>
                            )}
                        </Group>
                    );
                },
            },
        ],
        [pendingBatchId, onDispatch, onAdjust, onHistory, canRuleOnQc],
    );

    // Bad stock has no batch, so it rides as a line of its own beside the batches.
    const lines: PanelLine[] = item
        ? [
              ...item.batches,
              ...(item.bad
                  ? [
                        {
                            batchId: '',
                            batchCode: null,
                            manufacturingDate: null,
                            expiryDate: null,
                            qcStatus: null,
                            receivedAt: null,
                            availability: 'BLOCKED',
                            quantity: item.bad,
                            bad: true,
                        } as PanelLine,
                    ]
                  : []),
          ]
        : [];

    // Nothing here is a batch, so the batch's own fields would only ever read "No batch" and "—".
    const unbatched = !!item && item.batches.every((line) => !line.batchId);
    const hidden = new Set([
        ...(unbatched ? ['batchCode', 'qcStatus', 'expiryDate'] : []),
        // Read-only strips the actions column only when nothing in it can be pressed — adjust
        // survives, because it writes through the brand's own door rather than the site's.
        ...(readOnly && !onAdjust && !onHistory ? ['actions'] : []),
        // Unbatched stock offers Adjust and nothing else, so without it the column is empty.
        ...(unbatched && !onAdjust ? ['actions'] : []),
    ]);

    return (
        <>
            <Drawer
                opened={!!item}
                onClose={onClose}
                position="right"
                // The columns add up to 820 before cell padding; at 820 the Actions column ran off the edge.
                size={900}
                title={
                    <Text fz={16} fw={600} c={neutralColors.textPrimary}>
                        {item?.itemName ?? item?.itemId}
                    </Text>
                }
            >
                {item && (
                    <DataSheet<PanelLine>
                        data={lines}
                        columns={columns.filter((column) => !hidden.has(column.key))}
                        title={unbatched ? 'Stock' : 'Batches'}
                        subtitle={
                            unbatched
                                ? `${formatNumber(item.total, 1)} on hand`
                                : `${item.batches.length} batch${item.batches.length === 1 ? '' : 'es'} · ` +
                                  `${formatNumber(item.total, 1)} total · usable means released by QC and in date`
                        }
                        maxHeight="auto"
                    />
                )}
            </Drawer>

            {!readOnly && (
            <BatchActionModal
                action={action}
                batch={actionBatch}
                isMaterial={isMaterial}
                onClose={() => {
                    setAction(null);
                    setActionBatch(null);
                }}
                onDone={onChanged}
            />
            )}
        </>
    );
};

export default BatchDetailPanel;
